/**
 * Turnstile (el captcha invisible de Cloudflare) para pedir el código de acceso.
 *
 * Supabase, con la protección de captcha encendida (Auth → Attack Protection),
 * rechaza todo `signInWithOtp` que no traiga un token válido. Es lo que frena
 * crear cuentas en masa con un script: cada una necesitaría pasar el reto.
 *
 * Sin `VITE_TURNSTILE_SITE_KEY` no hace nada y devuelve `undefined`: así se
 * puede desplegar antes de configurarlo, y el acceso sigue igual hasta que se
 * pongan la clave aquí y el secreto en Supabase. Ojo: si se enciende en
 * Supabase SIN la clave aquí, nadie podrá pedir su código.
 *
 * El widget tiene que ser de tipo «Invisible» en Cloudflare: no se ve nada,
 * el reto corre por detrás en un momento.
 */
const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() || ''

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string
  remove: (widgetId: string) => void
}

let scriptLoading: Promise<TurnstileApi | null> | null = null

function loadTurnstile(): Promise<TurnstileApi | null> {
  const existing = (window as unknown as { turnstile?: TurnstileApi }).turnstile
  if (existing) return Promise.resolve(existing)
  scriptLoading ??= new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () => resolve((window as unknown as { turnstile?: TurnstileApi }).turnstile ?? null)
    script.onerror = () => { scriptLoading = null; resolve(null) }
    document.head.appendChild(script)
  })
  return scriptLoading
}

/** Un token nuevo para una sola llamada, o `undefined` si no está configurado o falló. */
export async function getCaptchaToken(): Promise<string | undefined> {
  if (!SITE_KEY) return undefined
  const api = await loadTurnstile()
  if (!api) return undefined
  return new Promise((resolve) => {
    const container = document.createElement('div')
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden'
    document.body.appendChild(container)
    let widgetId = ''
    const finish = (token: string | undefined) => {
      window.clearTimeout(timer)
      try { if (widgetId) api.remove(widgetId) } catch { /* ya no está */ }
      container.remove()
      resolve(token)
    }
    /* Si en 15 s no contesta, se sigue sin token: Supabase dirá que falta y se
       enseña su error, en vez de dejar el botón girando para siempre. */
    const timer = window.setTimeout(() => finish(undefined), 15_000)
    widgetId = api.render(container, {
      sitekey: SITE_KEY,
      callback: (token: string) => finish(token),
      'error-callback': () => finish(undefined),
      'expired-callback': () => finish(undefined),
    })
  })
}

/** Lo que se añade a `options` de `signInWithOtp`. */
export async function captchaOption(): Promise<{ captchaToken?: string }> {
  const token = await getCaptchaToken()
  return token ? { captchaToken: token } : {}
}
